import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import {
  CheckboxStatus,
  ModalRef,
  ModalModule,
  IconV2Module,
  InputV2Module,
  CheckboxV2Module,
  ButtonV2Module,
  SelectItemV2,
  ReactiveComponent,
  UNIT_DEFAULTS,
  UnitsService,
  ConvertUnitsApi,
} from '@schenck/rotec-components';
import { MODAL_WIDTH } from 'src/utils/constants/modal.const';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import {
  negativeZeroValidator,
  radiusValidator,
  valueRangeValidator,
} from 'src/pages/rotor-type-details/rotor-type-details.helper';

export interface LocationData {
  angle: number;
  correctionRadius: SelectItemV2;
  restricted: boolean;
  side: 'left' | 'right';
}

export interface LocationModalData {
  side: 'left' | 'right';
  location?: LocationData;
  enableRadii?: boolean;
}

@Component({
  selector: 'add-edit-location-modal',
  templateUrl: './add-edit-location-modal.component.html',
  styleUrls: ['./add-edit-location-modal.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ModalModule,
    IconV2Module,
    InputV2Module,
    CheckboxV2Module,
    ButtonV2Module,
    TranslatePipe,
    FormsModule,
    ReactiveFormsModule,
  ],
})
export class AddEditLocationModalComponent extends ReactiveComponent {
  private readonly unitsService = inject(UnitsService);
  private readonly convertUnitsApi = inject(ConvertUnitsApi);
  private readonly translate = inject(TranslateService);

  public modalRef = inject<ModalRef<LocationModalData>>(ModalRef);
  public defaultLengthUnit = UNIT_DEFAULTS.length;
  public locationForm: UntypedFormGroup;
  public width: string = MODAL_WIDTH;
  public title: string;
  public showDeleteButton: boolean;
  public restrictedValue: CheckboxStatus = 'unchecked';
  public valid: boolean = false;
  public enableRadius: boolean = false;

  private side: 'left' | 'right';

  constructor() {
    super();
    this.side = this.modalRef.data.side;
    this.title = this.modalRef.data.location ? 'Edit Location' : 'Add Location';
    this.showDeleteButton = !!this.modalRef.data.location;
    this.enableRadius = this.modalRef.data?.enableRadii;

    if (this.modalRef.data.location) {
      this.restrictedValue = this.modalRef.data.location.restricted ? 'checked' : 'unchecked';
    }
  }

  public ngOnInit() {
    super.ngOnInit();
    this.locationForm = new UntypedFormGroup({
      angle: new UntypedFormControl(0, [
        Validators.required,
        valueRangeValidator({ min: 0, max: 360 }),
        negativeZeroValidator(),
      ]),
      correctionRadius: new UntypedFormGroup(
        {
          name: new UntypedFormControl(null),
          value: new UntypedFormControl(null),
          unit: new UntypedFormControl(this.defaultLengthUnit),
        },
        [],
        radiusValidator(this.unitsService, this.convertUnitsApi)
      ),
    });
    this.locationForm.get('angle')?.patchValue(this.modalRef.data?.location?.angle);
    if (this.enableRadius) {
      this.locationForm
        .get('correctionRadius')
        ?.patchValue(this.modalRef.data?.location?.correctionRadius);
    }
  }

  public onChangeForm(eventValue: any, controlName: string) {
    const control = this.locationForm.get(controlName);

    if (!control) return;

    if (controlName === 'angle') {
      const newValue =
        typeof eventValue === 'number' ? eventValue : (eventValue?.value ?? control.value);

      control.setValue(newValue, { emitEvent: true });
    } else if (controlName === 'correctionRadius') {
      const group = control as UntypedFormGroup;
      const name = eventValue?.name ?? group.get('name')?.value;

      group.patchValue(
        {
          value: eventValue?.value,
          unit: eventValue?.unit,
          name,
        },
        { emitEvent: true }
      );
    }
  }

  public getAngleErrorMessage(errorMessage: { message: string; min: number; max: number }): string {
    return this.translate.instant(errorMessage.message, {
      min: errorMessage.min,
      max: errorMessage.max,
    });
  }

  public save(): void {
    const result: LocationData = {
      angle: this.locationForm.get('angle').value,
      correctionRadius: this.enableRadius ? this.locationForm.get('correctionRadius').value : '',
      restricted: this.restrictedValue === 'checked',
      side: this.side,
    };
    const noEmit: boolean =
      this.modalRef.data.location &&
      result.angle === this.modalRef.data.location?.angle &&
      result.correctionRadius === this.modalRef.data.location?.correctionRadius &&
      result.restricted === this.modalRef.data.location?.restricted;
    this.modalRef.emitAndClose(!noEmit ? result : null);
  }

  public cancel(): void {
    this.modalRef.close();
  }

  public deleteLocation(): void {
    this.modalRef.emitAndClose('Delete');
  }
}
